import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';

const DOMAIN_DATA = {
    dg: { title: 'Data Gathering & Diagnosis', weight: 3.0 },
    cm: { title: 'Clinical Management', weight: 4.5 },
    rto: { title: 'Relating to Others', weight: 3.0 }
};

const FEEDBACK_STATEMENTS = {
    // Data Gathering & Diagnosis
    'DG_HISTORY': 'Data gathering was insufficient to enable safe assessment of the condition/situation.',
    'DG_CONTEXT': 'Existing information about the case was insufficiently utilized.',
    'DG_PSYCHOSOCIAL': 'Relevant psychological or social information was insufficiently recognized or responded to.',
    'DG_STRUCTURE': 'Data gathering was unsystematic and/or disorganized.',
    'DG_DIAGNOSIS': 'The primary diagnosis or differential diagnosis was incorrect or incomplete.',
    'DG_RED_FLAGS': 'Failed to recognize or act on potential red flags.',
    'DG_ASSESSMENT': 'Does not demonstrate an adequate clinical assessment of the patient.',
    'DG_EXAM': 'Examination or tests requested were unnecessary or inappropriate.',
    'DG_RECORDS': 'Clinical records were not consulted where appropriate.',

    // Clinical Management
    'CM_PLAN': 'The management plan was incomplete, unsafe, or non-evidence based.',
    'CM_SHARED': 'There was insufficient evidence of shared decision-making with the patient.',
    'CM_SAFETY': 'Safety-netting or follow-up arrangements were inadequate.',
    'CM_PRESCRIBING': 'Prescribing was inappropriate, unsafe, or ignored relevant flags.',
    'CM_CONTINUITY': 'Aspects of continuity of care were ignored.',
    'CM_REFERRAL': 'A referral or specialist input was not appropriately considered.',
    'CM_CHALLENGING': 'Difficulty in managing a challenging situation or complex presentation.',
    'CM_PRIORITY': 'Failed to prioritize issues appropriately.',
    'CM_RESOURCE': 'Inefficient or inappropriate use of resources.',

    // Relating to Others
    'RTO_ICE': 'The patient\'s ideas, concerns, and expectations (ICE) were not sufficiently explored.',
    'RTO_EMPATHY': 'Empathy or rapport was insufficient throughout the consultation.',
    'RTO_PROFESSIONAL': 'The candidate failed to maintain a professional or patient-centered manner.',
    'RTO_JARGON': 'Too much medical jargon was used, or communication was not tailored to the patient.',
    'RTO_RESPECT': 'The patient was treated without sufficient respect or dignity.',
    'RTO_SENSITIVITY': 'Insensitivity to the patient\'s perspective or culture.',
    'RTO_ETHICAL': 'Ethical or professional boundaries were not maintained.',
    'RTO_PARTNER': 'Failed to involve the patient as a partner in their own care.'
};

const GradeBadge = ({ grade }) => {
    const colors = {
        'clear_pass': { bg: '#d4edda', text: '#155724', label: 'Clear Pass' },
        'pass': { bg: '#cce5ff', text: '#004085', label: 'Pass' },
        'fail': { bg: '#fff3cd', text: '#856404', label: 'Fail' },
        'clear_fail': { bg: '#f8d7da', text: '#721c24', label: 'Clear Fail' }
    };
    const style = colors[grade] || colors.fail;
    return (
        <span style={{
            padding: '4px 12px',
            borderRadius: '20px',
            background: style.bg,
            color: style.text,
            fontSize: '14px',
            fontWeight: 'bold',
            textTransform: 'uppercase'
        }}>
            {style.label}
        </span>
    );
};

const SCAReview = () => {
    const { sessionId } = useParams();
    // const navigate = useNavigate();
    const [score, setScore] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchScore = async () => {
            try {
                const res = await api.getSCAScore(sessionId);
                setScore(res.data);
            } catch (err) {
                console.error(err);
                if (err.response?.status === 404) {
                    // Try to trigger scoring if it hasn't happened
                    try {
                        const scoreRes = await api.scoreSCASession(sessionId);
                        setScore(scoreRes.data);
                    } catch (scoreErr) {
                        toast.error("Still processing your results. Please wait a moment.");
                    }
                } else {
                    toast.error("Failed to load review markers.");
                }
            } finally {
                setLoading(false);
            }
        };

        fetchScore();
    }, [sessionId]);

    if (loading) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
                <div className="spinner"></div>
                <h2 style={{ marginTop: '20px' }}>Finalizing Assessment...</h2>
            </div>
        );
    }

    if (!score) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <h2>Results Not Available</h2>
                <p>We couldn't find the markers for this session.</p>
                <Link to="/sca" className="btn btn-primary">Try Another Practice</Link>
            </div>
        );
    }

    return (
        <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '40px 20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <div>
                    <h1 className="text-primary" style={{ margin: 0 }}>SCA indicator Marks</h1>
                    <p className="text-muted">Indicative assessment based on RCGP domains.</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '48px', fontWeight: 'bold', color: score.total_points >= 6 ? '#28a745' : '#dc3545' }}>
                        {score.total_points.toFixed(1)} <span style={{ fontSize: '20px', color: '#888' }}>/ 10.5</span>
                    </div>
                    <p style={{ margin: 0, fontWeight: 'bold' }}>Total Score</p>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '40px' }}>
                {Object.keys(DOMAIN_DATA).map(key => (
                    <div key={key} className="card-bg" style={{ padding: '20px', borderRadius: '12px', border: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px' }}>
                        <h4 style={{ margin: 0, fontSize: '14px', textAlign: 'center', color: '#666' }}>{DOMAIN_DATA[key].title}</h4>
                        <GradeBadge grade={score[`${key}_grade`]} />
                        <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                            {score[`${key}_points`].toFixed(1)} <span style={{ fontSize: '14px', color: '#888' }}>/ {DOMAIN_DATA[key].weight.toFixed(1)}</span>
                        </div>
                    </div>
                ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '30px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
                    <div className="card-bg" style={{ padding: '30px', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
                        <h3 style={{ marginTop: 0 }}>Indicative Narrative Feedback</h3>
                        <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6', color: '#444' }}>
                            {score.overall_feedback}
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <div className="card-bg" style={{ padding: '20px', borderRadius: '12px', border: '1px solid #d4edda', background: '#f8fff9' }}>
                            <h4 style={{ color: '#155724', marginTop: 0 }}>Strengths</h4>
                            <ul style={{ paddingLeft: '20px' }}>
                                {score.strengths.map((s, i) => <li key={i} style={{ marginBottom: '8px' }}>{s}</li>)}
                            </ul>
                        </div>
                        <div className="card-bg" style={{ padding: '20px', borderRadius: '12px', border: '1px solid #fff3cd', background: '#fffcf5' }}>
                            <h4 style={{ color: '#856404', marginTop: 0 }}>Areas for Improvement</h4>
                            <ul style={{ paddingLeft: '20px' }}>
                                {score.areas_for_improvement.map((s, i) => <li key={i} style={{ marginBottom: '8px' }}>{s}</li>)}
                            </ul>
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
                    <div className="card-bg" style={{ padding: '20px', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
                        <h3 style={{ marginTop: 0, fontSize: '18px' }}>Feedback Statements</h3>
                        {Object.entries(score.feedback_statements).map(([domain, codes]) => (
                            <div key={domain} style={{ marginBottom: '20px' }}>
                                <h5 style={{ textTransform: 'uppercase', fontSize: '12px', color: '#888', marginBottom: '10px' }}>{DOMAIN_DATA[domain].title}</h5>
                                {codes.length > 0 ? (
                                    <ul style={{ paddingLeft: '15px', fontSize: '14px' }}>
                                        {codes.map((code, i) => (
                                            <li key={i} style={{ marginBottom: '10px' }}>{FEEDBACK_STATEMENTS[code] || code}</li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p style={{ fontSize: '13px', fontStyle: 'italic', color: '#999' }}>No specific issues identified.</p>
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="card-bg" style={{ padding: '20px', borderRadius: '12px', border: '1px solid #f8d7da', background: '#fff5f6' }}>
                        <h3 style={{ marginTop: 0, fontSize: '18px', color: '#721c24' }}>Clinical Safety</h3>
                        <div>
                            <p style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>Red Flags Identified:</p>
                            <ul style={{ fontSize: '14px', paddingLeft: '20px', color: '#155724' }}>
                                {score.red_flags_identified?.length > 0 ? score.red_flags_identified.map((f, i) => <li key={i}>{f}</li>) : <li>None explicitly noted.</li>}
                            </ul>
                            <p style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '5px', marginTop: '15px' }}>Red Flags Missed:</p>
                            <ul style={{ fontSize: '14px', paddingLeft: '20px', color: '#dc3545' }}>
                                {score.red_flags_missed?.length > 0 ? score.red_flags_missed.map((f, i) => <li key={i}>{f}</li>) : <li>None missed. Excellent safety screening.</li>}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div style={{ marginTop: '50px', display: 'flex', justifyContent: 'center', gap: '20px' }}>
                <Link to="/sca" className="btn btn-primary" style={{ padding: '12px 30px', borderRadius: '8px', textDecoration: 'none' }}>Try Another Practice</Link>
                <button onClick={() => window.print()} className="btn btn-secondary" style={{ padding: '12px 30px', borderRadius: '8px' }}>Download PDF Report</button>
            </div>
        </div>
    );
};

export default SCAReview;
